import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { createRetryLoader, onWakeOrOnline } from "../lib/retryLoader";
import { authAPI } from "../services/api";
import { entitlementFrom, resolveEffectiveTier } from "../lib/entitlements";
import { clearUserStorage, dropOrphanKeys } from "../lib/userStorage";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasFetchedRef = useRef(false);
  // Has whoami answered for this user in this session? Until it has, the tier
  // is only known if the stored one grants Pro (resolveEffectiveTier).
  const [tierFresh, setTierFresh] = useState(false);

  // Load user from localStorage, then refresh from backend
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    dropOrphanKeys();

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        // whoami (tier, email) is read by the retry effect below.
      } catch (e) {
        console.error("Failed to parse stored user:", e);
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  // Read whoami until it answers: a failure (API unreachable at launch) is
  // retried with backoff and on tab-visible / back-online, never taken as
  // "lapsed". A 401/403 stops it (the session interceptor handles that).
  const userKey = user?.id ?? user?.email ?? user?.user_id ?? null;   // user_id: a copy a profile overwrote
  useEffect(() => {
    if (!userKey || tierFresh) return undefined;
    const loader = createRetryLoader({
      fetchValue: async () => (await authAPI.whoami()) || {},
      onAnswer: (fresh) => {
        setUser((prev) => {
          if (!prev) return prev;               // signed out meanwhile
          const merged = { ...prev, ...fresh };
          localStorage.setItem("user", JSON.stringify(merged));
          return merged;
        });
        setTierFresh(true);
      },
      shouldRetry: (error) => ![401, 403].includes(error?.response?.status),
    });
    loader.start();
    const off = onWakeOrOnline(loader.retryNow);
    return () => { off(); loader.stop(); };
  }, [userKey, tierFresh]);

  // Re-read who this is (whoami): the real id and the tier whose
  // entitlements apply now. Sign-up and sign-in only know a placeholder (no
  // effective_tier: a trial athlete read as lapsed and was offered "Upgrade to
  // unlock your AI coach", Sam, 2026-10-25), and onboarding's pricing step can
  // start a trial. A failure keeps what we have.
  const refreshUser = async () => {
    try {
      const fresh = await authAPI.whoami();
      setUser((prev) => {
        if (!prev) return prev;                 // signed out meanwhile
        const merged = { ...prev, ...fresh };
        localStorage.setItem("user", JSON.stringify(merged));
        return merged;
      });
      setTierFresh(true);
      return fresh;
    } catch {
      return null;
    }
  };

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
    console.log("User logged in:", userData.username || userData.athlete?.firstname);
    // A placeholder until whoami answers (the retry effect above reads it).
    setTierFresh(false);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error("Logout API error:", error);
    }
    setUser(null);
    setTierFresh(false);
    // Everything this browser kept for that person, not just 'user' (#46).
    // Once now, and once after the outgoing contexts have torn down, so a
    // late write from them (the v2 client's queue, the roster cache) goes too.
    clearUserStorage();
    setTimeout(() => { clearUserStorage(); }, 2000);
    console.log("User logged out");
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshUser, tierFresh }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook
export const useAuth = () => useContext(AuthContext);

// Entitlement helper bound to the current user. Mirrors the backend
// COMPONENT_TIER registry; gate AI-coach UI on `allowed(component)`.
// Uses effective_tier (lapsed-in-trial already folded up to pro).
export const useEntitlement = () => {
  const { user, tierFresh } = useAuth();
  // Unknown tier (whoami not answered, stored tier doesn't grant): `allowed`
  // stays fail-closed, `denied`/`isLapsed` stay false — never the lapsed copy.
  return entitlementFrom(resolveEffectiveTier(user, tierFresh));
};
